import React from 'react';
import styled from 'styled-components';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SendIcon from '@mui/icons-material/Send';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import type { QueuedMessage } from '../../utils/messageQueue';

// Bolha de uma mensagem que espera o turno atual terminar. Fica à direita, como
// a do usuário, mas tracejada e sem markdown: ainda não foi enviada e pode ser
// editada ou removida.

const Row = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
  margin: 14px 0 0;
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  max-width: 80%;
  @media (max-width: 900px) {
    max-width: 100%;
    margin-right: 10px;
  }
`;

const Bubble = styled.div`
  color: ${({ theme }) => theme.font_color};
  background-color: transparent;
  border: 1.5px dashed ${({ theme }) => theme.text_secondary};
  border-radius: 20px;
  padding: 10px 14px;
  font-family: 'Sora', 'Lexend', sans-serif;
  font-size: 1rem;
  line-height: 1.8rem;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  opacity: 0.85;
`;

const Files = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
`;

const FileChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 0.75rem;
  padding: 2px 8px;
  border-radius: 10px;
  background: ${({ theme }) => theme.user_message_background};
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.text_secondary};
  font-family: 'Sora', 'Lexend', sans-serif;
`;

const Action = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  border: none;
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 6px;
  &:hover { background: ${({ theme }) => theme.user_message_background}; }
`;

interface Props {
  item: QueuedMessage;
  // Fila pausada (após "Parar" ou com o chat em background): não sai sozinha.
  held: boolean;
  onSendNow: (id: string) => void;
  onEdit: (id: string) => void;
  onRemove: (id: string) => void;
}

function QueuedMessageRow({ item, held, onSendNow, onEdit, onRemove }: Props) {
  return (
    <Row data-testid="queued-message-row" data-download-exclude>
      <Stack>
        <Bubble>
          {item.content}
          {item.files && item.files.length > 0 && (
            <Files>
              {item.files.map((f) => (
                <FileChip key={f.name}><AttachFileIcon style={{ fontSize: 12 }} />{f.name}</FileChip>
              ))}
            </Files>
          )}
        </Bubble>
        <Meta>
          <ScheduleIcon style={{ fontSize: 14 }} />
          <span>{held ? 'Na fila · envio pausado' : 'Na fila · sai quando a resposta terminar'}</span>
          <Action
            type="button"
            onClick={() => onSendNow(item.id)}
            aria-label="Enviar agora"
            title={held ? 'Enviar esta mensagem' : 'Interrompe a resposta atual e envia esta mensagem'}
          >
            <SendIcon style={{ fontSize: 13 }} /> Enviar agora
          </Action>
          <Action type="button" onClick={() => onEdit(item.id)} aria-label="Editar mensagem na fila">
            <EditOutlinedIcon style={{ fontSize: 13 }} /> Editar
          </Action>
          <Action type="button" onClick={() => onRemove(item.id)} aria-label="Remover da fila">
            <CloseIcon style={{ fontSize: 13 }} /> Remover
          </Action>
        </Meta>
      </Stack>
    </Row>
  );
}

export default QueuedMessageRow;
